import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { apiUrl } from '../utils/apiBase';
import { RefreshCw, Smartphone, CheckCircle, AlertCircle, Clock, X } from 'lucide-react';

export function QRLogin({ onSuccess, onError, onClose }) {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [status, setStatus] = useState('generating'); // generating, waiting, authenticated, expired, error
  const [expiresIn, setExpiresIn] = useState(0);
  
  // Usa useRef per evitare problemi di closure con isPolling
  const isPollingRef = useRef(false);
  const pollingIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  useEffect(() => {
    generateQRSession();
    return () => {
      // Cleanup polling on unmount
      console.log('🧹 Cleanup QRLogin component');
      isPollingRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const generateQRSession = async () => {
    try {
      console.log('🔄 Generazione nuova sessione QR...');
      setStatus('generating');
      
      const response = await fetch(apiUrl('/api/auth/qr/generate'), { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Errore generazione sessione QR');
      }
      
      const data = await response.json();
      console.log('📋 Sessione QR generata:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Errore generazione QR');
      }
      
      setSessionId(data.sessionId);
      setExpiresIn(data.expiresIn);
      console.log(`⏰ Timer impostato a: ${data.expiresIn} secondi`);
      
      // Genera QR code che punta a pagina mobile
      const baseUrl = window.location.origin;
      const mobileLoginUrl = `${baseUrl}/mobile-login/${data.sessionId}`;
      console.log('📱 URL mobile login:', mobileLoginUrl);
      
      const qrDataUrl = await QRCode.toDataURL(mobileLoginUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#1f2937', // Colore scuro per il QR
          light: '#ffffff' // Sfondo bianco
        }
      });
      
      setQrCodeUrl(qrDataUrl);
      setStatus('waiting');
      console.log('✅ QR code generato e componente pronto');
      
      // Inizia polling per verificare autenticazione
      pollForAuthentication(data.sessionId);
      
      // Timer separato per countdown (ogni secondo)
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      countdownIntervalRef.current = setInterval(() => {
        setExpiresIn(prev => {
          const newValue = Math.max(0, prev - 1);
          if (newValue <= 0) {
            console.log('⏰ Countdown terminato');
            clearInterval(countdownIntervalRef.current);
            setStatus('expired');
            isPollingRef.current = false;
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
          }
          return newValue;
        });
      }, 1000);
      
    } catch (error) {
      console.error('❌ Errore generazione QR:', error);
      setStatus('error');
      onError?.(error.message);
    }
  };

  const pollForAuthentication = (sessionId) => {
    console.log(`🔍 Avvio polling per sessione: ${sessionId}`);
    
    // Stop any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    isPollingRef.current = true;
    
    pollingIntervalRef.current = setInterval(async () => {
      if (!isPollingRef.current) {
        console.log('⏹️ Polling fermato');
        clearInterval(pollingIntervalRef.current);
        return;
      }
      
      try {
        console.log(`📡 Polling stato sessione: ${sessionId}`);
        const response = await fetch(apiUrl(`/api/auth/qr/status/${sessionId}`));
        const session = await response.json();
        
        console.log('📊 Risposta polling:', session);
        
        // Aggiorna timer solo se valido
        if (session.expiresIn !== undefined) {
          setExpiresIn(session.expiresIn);
        }
        
        if (session.status === 'authenticated') {
          console.log('✅ Autenticazione QR completata!', session);
          clearInterval(pollingIntervalRef.current);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          isPollingRef.current = false;
          setStatus('authenticated');
          
          // Chiama callback successo con token e dati utente
          onSuccess?.(session.token, session.user);
          
        } else if (session.status === 'expired' || session.status === 'invalid') {
          console.log('⏰ Sessione QR scaduta o non valida');
          clearInterval(pollingIntervalRef.current);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          isPollingRef.current = false;
          setStatus('expired');
          
        } else if (session.expiresIn <= 0) {
          console.log('⏰ Timer scaduto');
          clearInterval(pollingIntervalRef.current);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          isPollingRef.current = false;
          setStatus('expired');
        }
        
      } catch (error) {
        console.error('❌ Errore polling QR:', error);
        clearInterval(pollingIntervalRef.current);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        isPollingRef.current = false;
        setStatus('error');
        onError?.(error.message);
      }
    }, 2000); // Polling ogni 2 secondi
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderStatus = () => {
    switch (status) {
      case 'generating':
        return (
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-gray-400">Generazione QR code...</p>
          </div>
        );
        
      case 'waiting':
        return (
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 text-blue-400">
              <Smartphone size={20} />
              <span className="font-medium">In attesa di scansione</span>
            </div>
            
            <div className="text-sm text-gray-400 space-y-1">
              <p>📱 Scansiona il QR code con il telefone</p>
              <p>🔐 Inserisci le tue credenziali sul mobile</p>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-gray-300 bg-gray-800/30 py-2 px-4 rounded-lg">
              <Clock size={16} className="text-amber-400" />
              <span className="text-sm font-mono font-medium">
                Scade tra: <span className="text-amber-400">{formatTime(expiresIn)}</span>
              </span>
            </div>
          </div>
        );
        
      case 'authenticated':
        return (
          <div className="text-center text-green-400">
            <CheckCircle size={48} className="mx-auto mb-3" />
            <p className="font-medium">Autenticazione completata!</p>
            <p className="text-sm text-gray-400 mt-1">Accesso in corso...</p>
          </div>
        );
        
      case 'expired':
        return (
          <div className="text-center text-amber-400">
            <AlertCircle size={48} className="mx-auto mb-3" />
            <p className="font-medium">QR code scaduto</p>
            <p className="text-sm text-gray-400 mt-1">Clicca "Rigenera" per un nuovo codice</p>
          </div>
        );
        
      case 'error':
        return (
          <div className="text-center text-red-400">
            <AlertCircle size={48} className="mx-auto mb-3" />
            <p className="font-medium">Errore connessione</p>
            <p className="text-sm text-gray-400 mt-1">Clicca "Rigenera" per riprovare</p>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-full text-gray-400 hover:text-white transition-all duration-200 backdrop-blur-sm"
          title="Chiudi QR Login"
        >
          <X size={20} />
        </button>
      )}

      {/* QR Code Display */}
      {qrCodeUrl && (status === 'waiting' || status === 'authenticated') && (
        <div className="flex justify-center">
          <div className="bg-white p-6 rounded-2xl shadow-2xl ring-1 ring-gray-300/20">
            <img 
              src={qrCodeUrl} 
              alt="QR Login Code" 
              className="w-64 h-64 rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Status Display */}
      <div className="min-h-[120px] flex items-center justify-center">
        {renderStatus()}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center">
        {(status === 'expired' || status === 'error') && (
          <button
            onClick={() => {
              console.log('🔄 Rigenerazione QR richiesta');
              // Stop tutti i timer esistenti
              isPollingRef.current = false;
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
              }
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
              }
              generateQRSession();
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <RefreshCw size={18} />
            Rigenera QR Code
          </button>
        )}
      </div>

      {/* Instructions */}
      <div className="text-center text-xs text-gray-400 space-y-2 bg-gray-900/30 p-4 rounded-lg border border-gray-800/50">
        <div className="flex items-center justify-center gap-2 text-gray-300 font-medium mb-2">
          <Smartphone size={14} />
          <span>Istruzioni per l'uso</span>
        </div>
        <p>• Il QR code è valido per 5 minuti</p>
        <p>• Usa lo stesso telefono connesso alla rete locale</p>
        <p>• Dopo il login, puoi chiudere la pagina mobile</p>
      </div>
    </div>
  );
}

export default QRLogin;
